import React, { useEffect, useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import AppLayout from '../components/AppLayout';
import api from '../utils/api';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import { useSocketEvent } from '../context/SocketContext';
import { PaymentBadge } from '../components/Badges';
import {
  Search,
  Receipt,
  Save,
  IndianRupee,
  ArrowUpDown,
  Plus,
  History,
  FileText,
  Calendar,
  ShieldAlert,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  User,
  Clock,
  Printer,
  Layers,
} from 'lucide-react';
import BillPrintModal from '../components/BillPrintModal';
import { CLINIC_NAME, CLINIC_PHONE, CLINIC_ADDRESS } from '../utils/clinicConfig';
import WhatsAppButton from '../components/WhatsAppButton';

// ── helpers ───────────────────────────────────────────────────────────────────
const PAGE_SIZE_OPTIONS = [10, 25, 50, 75, 100];

const SORT_OPTIONS = [
  { key: 'tokenDate-desc', label: 'Date (newest)' },
  { key: 'tokenDate-asc', label: 'Date (oldest)' },
  { key: 'name-asc',      label: 'Name (A–Z)' },
  { key: 'name-desc',     label: 'Name (Z–A)' },
  { key: 'paymentStatus-asc',  label: 'Pending first' },
  { key: 'paymentStatus-desc', label: 'Paid first' },
  { key: 'balance-desc',  label: 'Highest pending balance' },
  { key: 'amount-desc',   label: 'Highest bill amount' },
];

// DB stores 'unpaid'; we show "Pending" everywhere in the UI.
// This map is the single source of truth for display labels.
const STATUS_LABELS = { unpaid: 'Pending', partial: 'Partial', paid: 'Paid' };

const FILTER_OPTIONS = [
  { key: 'all',    label: 'All' },
  { key: 'unpaid', label: 'Pending' },   // display "Pending", pass 'unpaid' to DB
  { key: 'partial',label: 'Partial' },
  { key: 'paid',   label: 'Paid' },
];

function netTotal(billing) {
  const amount = billing?.amount || 0;
  const discount = billing?.discount || 0;
  const taxPct = billing?.taxPercent || 0;
  const afterDiscount = Math.max(amount - discount, 0);
  return afterDiscount + afterDiscount * (taxPct / 100);
}

function pendingBalance(billing) {
  return Math.max(netTotal(billing) - (billing?.amountPaid || 0), 0);
}

// Derive payment status automatically from amounts.
// This is called whenever amount/discount/tax/amountPaid changes.
function deriveStatus(net, amountPaid) {
  const paid = Number(amountPaid) || 0;
  const total = Number(net) || 0;
  if (total === 0) return 'unpaid';
  if (paid >= total) return 'paid';
  if (paid > 0) return 'partial';
  return 'unpaid';
}

function fmt(n) {
  return (n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });
}

function fmtDateTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
}



// ── BillingDatePicker — calendar popup with available dates highlighted ───────
function BillingDatePicker({ selectedDate, onChange, availableDates }) {
  const [open, setOpen] = React.useState(false);
  const [viewYear, setViewYear]   = React.useState(() => {
    const d = selectedDate && selectedDate !== 'all' ? new Date(selectedDate) : new Date();
    return d.getFullYear();
  });
  const [viewMonth, setViewMonth] = React.useState(() => {
    const d = selectedDate && selectedDate !== 'all' ? new Date(selectedDate) : new Date();
    return d.getMonth();
  });
  const btnRef  = React.useRef(null);
  const [pos, setPos] = React.useState({ top:0, left:0 });

  const todayStr = new Date().toISOString().slice(0,10);
  const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  const DAY_LABELS = ['Su','Mo','Tu','We','Th','Fr','Sa'];

  function open_() {
    if (!btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    let left = r.left;
    if (left + 290 > window.innerWidth - 8) left = window.innerWidth - 298;
    setPos({ top: r.bottom + 6, left: Math.max(left, 8) });
    setOpen(true);
  }

  function prevMonth() {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y-1); }
    else setViewMonth(m => m-1);
  }
  function nextMonth() {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y+1); }
    else setViewMonth(m => m+1);
  }

  const firstDay = new Date(viewYear, viewMonth, 1).getDay();
  const daysInMonth = new Date(viewYear, viewMonth+1, 0).getDate();
  const cells = [];
  for (let i=0; i<firstDay; i++) cells.push(null);
  for (let d=1; d<=daysInMonth; d++) cells.push(d);

  const label = selectedDate && selectedDate !== 'all'
    ? new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' })
    : 'All dates';

  return (
    <>
      <button ref={btnRef} type="button" onClick={open_}
        className="btn btn-outline btn-sm"
        style={{ display:'flex', alignItems:'center', gap:7, fontWeight:600, minWidth:148 }}>
        <Calendar size={14} />
        {label}
        <span style={{ marginLeft:'auto', opacity:0.5, fontSize:10 }}>▼</span>
      </button>

      {selectedDate !== 'all' && (
        <button className="btn btn-ghost btn-sm" onClick={() => onChange('all')}
          style={{ padding:'4px 8px', fontSize:12 }} title="Show all dates">
          × All
        </button>
      )}

      {open && ReactDOM.createPortal(
        <>
          <div onClick={() => setOpen(false)} style={{ position:'fixed', inset:0, zIndex:3998 }} />
          <div style={{
            position:'fixed', top:pos.top, left:pos.left, zIndex:3999,
            background:'var(--color-surface)',
            border:'2px solid var(--color-border)',
            borderRadius:12,
            boxShadow:'var(--shadow-lg)',
            width:290, overflow:'hidden',
          }}>
            {/* "All dates" option at top */}
            <div style={{ padding:'10px 14px', borderBottom:'1px solid var(--color-border)' }}>
              <button type="button"
                onClick={() => { onChange('all'); setOpen(false); }}
                style={{
                  width:'100%', padding:'7px 12px', borderRadius:8, cursor:'pointer', textAlign:'left',
                  fontWeight:700, fontSize:13,
                  background: selectedDate==='all' ? 'var(--color-primary)' : 'var(--color-surface-raised)',
                  color: selectedDate==='all' ? '#fff' : 'var(--color-ink)',
                  border: `1px solid ${selectedDate==='all' ? 'var(--color-primary)' : 'var(--color-border)'}`,
                }}>
                All dates
                {availableDates.size > 0 && (
                  <span style={{ fontSize:11, fontWeight:400, marginLeft:8, opacity:0.7 }}>
                    {availableDates.size} date{availableDates.size!==1?'s':''} with records
                  </span>
                )}
              </button>
            </div>

            {/* Month navigation */}
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 14px 6px' }}>
              <button type="button" onClick={prevMonth} className="btn btn-ghost btn-sm" style={{ padding:'4px 8px' }}>‹</button>
              <span style={{ fontWeight:700, fontSize:13 }}>{MONTHS[viewMonth]} {viewYear}</span>
              <button type="button" onClick={nextMonth} className="btn btn-ghost btn-sm" style={{ padding:'4px 8px' }}>›</button>
            </div>

            {/* Day labels */}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', padding:'0 10px', gap:2, marginBottom:4 }}>
              {DAY_LABELS.map(d => (
                <div key={d} style={{ textAlign:'center', fontSize:10, fontWeight:700, color:'var(--color-ink-faint)', padding:'2px 0' }}>{d}</div>
              ))}
            </div>

            {/* Date cells */}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', padding:'0 10px 12px', gap:2 }}>
              {cells.map((day, i) => {
                if (!day) return <div key={i} />;
                const mm = String(viewMonth+1).padStart(2,'0');
                const dd = String(day).padStart(2,'0');
                const dateStr = `${viewYear}-${mm}-${dd}`;
                const hasRecords = availableDates.size === 0 || availableDates.has(dateStr);
                const isSelected = selectedDate === dateStr;
                const isToday    = dateStr === todayStr;
                return (
                  <button key={i} type="button"
                    disabled={availableDates.size > 0 && !hasRecords}
                    onClick={() => { onChange(dateStr); setOpen(false); }}
                    style={{
                      aspectRatio:'1', borderRadius:6, border:'none', cursor: hasRecords ? 'pointer' : 'not-allowed',
                      fontSize:12, fontWeight: hasRecords ? 700 : 400,
                      background: isSelected ? 'var(--color-primary)'
                               : isToday && hasRecords ? 'var(--color-primary-soft)'
                               : 'transparent',
                      color: isSelected ? '#fff'
                           : hasRecords ? (isToday ? 'var(--color-primary)' : 'var(--color-ink)')
                           : 'var(--color-ink-faint)',
                      opacity: hasRecords ? 1 : 0.35,
                      outline: isToday && !isSelected ? '1px solid var(--color-primary)' : 'none',
                    }}>
                    {day}
                    {hasRecords && !isSelected && (
                      <div style={{ width:4, height:4, borderRadius:'50%', background:'var(--color-primary)', margin:'1px auto 0' }} />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </>,
        document.body
      )}
    </>
  );
}

// ── Bill WhatsApp message builder ─────────────────────────────────────────────
function buildBillMessage(p, total, paid, pending) {
  const clinic   = typeof CLINIC_NAME    !== 'undefined' ? CLINIC_NAME    : 'Our Clinic';
  const phone    = typeof CLINIC_PHONE   !== 'undefined' ? CLINIC_PHONE   : '';
  const address  = typeof CLINIC_ADDRESS !== 'undefined' ? CLINIC_ADDRESS : '';
  const status   = p.billing?.paymentStatus === 'paid' ? 'PAID IN FULL'
                 : p.billing?.paymentStatus === 'partial' ? 'PARTIALLY PAID'
                 : 'PAYMENT DUE';
  return [
    `*${clinic}*`,
    address ? address : null,
    ``,
    `*BILLING SUMMARY*`,
    `Patient : ${p.name}`,
    `Date    : ${p.tokenDate}`,
    p.appointmentSlot ? `Slot    : ${p.appointmentSlot}` : null,
    p.billing?.invoiceNumber ? `Invoice : ${p.billing.invoiceNumber}` : null,
    ``,
    `Total   : Rs. ${fmt(total)}`,
    paid > 0 ? `Paid    : Rs. ${fmt(paid)}` : null,
    pending > 0 ? `*Pending : Rs. ${fmt(pending)}*` : null,
    `Status  : ${status}`,
    ``,
    pending > 0 ? `Kindly clear the pending balance at your earliest convenience.` : `Thank you for your payment!`,
    phone ? `For queries, call ${phone}.` : null,
  ].filter(l => l !== null).join('\n');
}

// ── BillingPatientCard — single visit row used in date/status grouped views ──
function BillingPatientCard({ p, isAdmin, editingId, setEditingId, correctionId, setCorrectionId, startEdit, startCorrection, setPrintBillPatient, billingForm, setBillingForm, paymentForm, setPaymentForm, saving, recordingPayment, saveBilling, recordPayment, correctionForm, setCorrectionForm, correctionReason, setCorrectionReason, correcting, applyCorrection }) {
  const total        = netTotal(p.billing);
  const pending      = pendingBalance(p.billing);
  const isEditing    = editingId    === p._id;
  const isCorrecting = correctionId === p._id;
  return (
    <div className="card" style={{ padding:16 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:10 }}>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontWeight:700, fontSize:14 }}>{p.name}</div>
          <div style={{ fontSize:12, color:'var(--color-ink-faint)', fontFamily:'var(--font-mono)', marginTop:2 }}>
            {p.patientUID} · Token #{p.tokenNumber} · {p.tokenDate}
            {p.billing?.invoiceNumber ? ` · ${p.billing.invoiceNumber}` : ''}
          </div>
          <div style={{ fontSize:12, color:'var(--color-ink-faint)', marginTop:2, display:'flex', gap:10, flexWrap:'wrap' }}>
            {p.age && <span>{p.age}y, {p.gender}</span>}
            {p.phone && <span>Ph: {p.phone}</span>}
            {p.appointmentSlot && <span style={{ color:'var(--color-primary)', fontWeight:600 }}>{p.appointmentSlot}</span>}
            {p.reasonForVisit && <span style={{ fontStyle:'italic' }}>{p.reasonForVisit}</span>}
          </div>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
          <NumCell label="Total"   value={`₹${fmt(total)}`} />
          <NumCell label="Paid"    value={`₹${fmt(p.billing?.amountPaid)}`} color="var(--color-success)" />
          <NumCell label="Pending" value={`₹${fmt(pending)}`} color={pending>0?'var(--color-warning)':'var(--color-ink-faint)'} />
          <PaymentBadge status={p.billing?.paymentStatus||'unpaid'} />
          <div style={{ display:'flex', gap:6 }}>
            {p.phone && (
              <WhatsAppButton
                patient={{ ...p, name: p.name, phone: p.phone,
                  _billMessage: buildBillMessage(p, total, p.billing?.amountPaid||0, pending) }}
                slot={p.appointmentSlot}
                date={p.tokenDate}
                tokenNumber={p.tokenNumber}
                size="xs"
                billMode
              />
            )}
            <button className="btn btn-outline btn-sm" onClick={() => setPrintBillPatient(p)}><Printer size={13}/> Print</button>
            <button className="btn btn-outline btn-sm" onClick={() => isEditing ? setEditingId(null) : startEdit(p)}>{isEditing ? 'Close' : 'Manage'}</button>
            {isAdmin && <button className="btn btn-sm" style={{ background:'var(--color-warning-soft)', color:'var(--color-warning)' }} onClick={() => isCorrecting ? setCorrectionId(null) : startCorrection(p)}><ShieldAlert size={13}/>{isCorrecting ? 'Close' : 'Correct'}</button>}
          </div>
        </div>
      </div>
      {isEditing && (
        <div style={{ marginTop:12 }}>
          <ManagePanel p={p} billingForm={billingForm} setBillingForm={setBillingForm} paymentForm={paymentForm} setPaymentForm={setPaymentForm} saving={saving} recordingPayment={recordingPayment} onSave={() => saveBilling(p._id, p)} onRecordPayment={() => recordPayment(p)} />
        </div>
      )}
      {isCorrecting && isAdmin && (
        <div style={{ marginTop:12 }}>
          <CorrectionPanel correctionForm={correctionForm} setCorrectionForm={setCorrectionForm} correctionReason={correctionReason} setCorrectionReason={setCorrectionReason} correcting={correcting} onApply={() => applyCorrection(p._id)} />
        </div>
      )}
    </div>
  );
}

// ── main component ─────────────────────────────────────────────────────────────
export default function BillingPage() {
  const { user } = useAuth();
  const { showToast } = useToast();
  const isAdmin = user?.isAdmin === true;

  const [patients, setPatients] = useState([]);
  const [totals, setTotals] = useState({ billed: 0, collected: 0, balance: 0 });
  const [loading, setLoading] = useState(true);

  const [selectedDate, setSelectedDate] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [availableDates, setAvailableDates] = useState(new Set());
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState('tokenDate-desc');
  const [showStats, setShowStats] = useState(() => localStorage.getItem('billing_showStats') !== 'false');
  const toggleStats = () => setShowStats(v => { localStorage.setItem('billing_showStats', !v); return !v; });
  const [groupBy, setGroupBy] = useState('patient');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [totalCount, setTotalCount] = useState(0);

  const [editingId, setEditingId] = useState(null);
  const [printBillPatient, setPrintBillPatient] = useState(null);
  const [billingForm, setBillingForm] = useState({});
  const [paymentForm, setPaymentForm] = useState({ amount: '', method: 'cash', note: '' });
  const [saving, setSaving] = useState(false);
  const [recordingPayment, setRecordingPayment] = useState(false);

  const [correctionId, setCorrectionId] = useState(null);
  const [correctionForm, setCorrectionForm] = useState({});
  const [correctionReason, setCorrectionReason] = useState('');
  const [correcting, setCorrecting] = useState(false);

  // ── fetch ──────────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/patients/billing/daily', {
        params: { date: selectedDate, page, limit },
      });
      setPatients(res.data.patients);
      setTotals(res.data.totals);
      setTotalCount(res.data.total);
      if (res.data.availableDates) setAvailableDates(new Set(res.data.availableDates));
    } catch (err) {
      showToast('Could not load billing records.', 'error');
    } finally {
      setLoading(false);
    }
  }, [selectedDate, page, limit, showToast]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [selectedDate, limit, statusFilter]);

  useSocketEvent('patient:billing-updated', (payload) => {
    if (!payload?.patient) return;
    setPatients((prev) => prev.map((p) =>
      p._id === payload.patient._id ? payload.patient : p
    ));
    load();
  });
  useSocketEvent('queue:reset', load);

  // ── helpers ────────────────────────────────────────────────────────────────
  function startEdit(patient) {
    setEditingId(patient._id);
    setCorrectionId(null);
    // Note: amountPaid is intentionally NOT in the billing form here.
    // Changing bill details (amount, discount, tax) should NOT overwrite
    // what has already been paid — that's only touched via "Record a Payment"
    // or admin correction. This prevents payment history from being lost.
    setBillingForm({
      invoiceNumber: patient.billing?.invoiceNumber || '',
      amount:        patient.billing?.amount || 0,
      discount:      patient.billing?.discount || 0,
      taxPercent:    patient.billing?.taxPercent || 0,
      dueDate:       patient.billing?.dueDate ? patient.billing.dueDate.slice(0, 10) : '',
      notes:         patient.billing?.notes || '',
    });
    setPaymentForm({ amount: '', method: 'cash', note: '' });
  }

  function startCorrection(patient) {
    setCorrectionId(patient._id);
    setEditingId(null);
    setCorrectionForm({
      invoiceNumber:  patient.billing?.invoiceNumber || '',
      amount:         patient.billing?.amount || 0,
      discount:       patient.billing?.discount || 0,
      taxPercent:     patient.billing?.taxPercent || 0,
      amountPaid:     patient.billing?.amountPaid || 0,
      paymentStatus:  patient.billing?.paymentStatus || 'unpaid',
      paymentMethod:  patient.billing?.paymentMethod || 'none',
      dueDate:        patient.billing?.dueDate ? patient.billing.dueDate.slice(0, 10) : '',
      notes:          patient.billing?.notes || '',
    });
    setCorrectionReason('');
  }

  // "Save Billing" — only updates invoice, bill amount, discount, tax, due date,
  // and notes. Does NOT touch amountPaid, paymentMethod, or paymentStatus.
  // Payment status is re-derived server-side from the new net total vs. what
  // has already been paid, so the status badge always reflects reality.
  async function saveBilling(patientId, currentPatient) {
    setSaving(true);
    try {
      const currentPaid  = currentPatient.billing?.amountPaid || 0;
      const currentNet   = netTotal({
        amount:     Number(billingForm.amount),
        discount:   Number(billingForm.discount),
        taxPercent: Number(billingForm.taxPercent),
      });
      const autoStatus = deriveStatus(currentNet, currentPaid);

      const res = await api.put(`/patients/${patientId}/billing`, {
        invoiceNumber: billingForm.invoiceNumber,
        amount:        Number(billingForm.amount),
        discount:      Number(billingForm.discount),
        taxPercent:    Number(billingForm.taxPercent),
        dueDate:       billingForm.dueDate || null,
        notes:         billingForm.notes,
        // Auto-derived status so the badge updates correctly after bill changes
        paymentStatus: autoStatus,
        // amountPaid is deliberately omitted — previous payments are preserved
      });
      showToast('Billing details saved.', 'success');
      setPatients((prev) => prev.map((p) => (p._id === patientId ? res.data.patient : p)));
      setEditingId(null);
      load();
    } catch (err) {
      showToast(err.response?.data?.message || 'Could not save billing.', 'error');
    } finally {
      setSaving(false);
    }
  }

  // "Record a Payment" — adds to the running paid total and appends a
  // history entry. Payment status is auto-derived from new paid vs. net total.
  async function recordPayment(patient) {
    if (!paymentForm.amount || Number(paymentForm.amount) <= 0) {
      showToast('Enter a payment amount greater than zero.', 'error');
      return;
    }
    setRecordingPayment(true);
    try {
      const currentPaid  = patient.billing?.amountPaid || 0;
      const newAmountPaid = currentPaid + Number(paymentForm.amount);
      const total        = netTotal(patient.billing);
      const newStatus    = deriveStatus(total, newAmountPaid);

      const res = await api.put(`/patients/${patient._id}/billing`, {
        amountPaid:    newAmountPaid,
        paymentStatus: newStatus,
        paymentMethod: paymentForm.method,
        newPayment: {
          amount: Number(paymentForm.amount),
          method: paymentForm.method,
          note:   paymentForm.note,
        },
      });
      showToast(
        `Payment of ₹${fmt(Number(paymentForm.amount))} recorded. Status: ${STATUS_LABELS[newStatus]}.`,
        'success'
      );
      setPatients((prev) => prev.map((p) => (p._id === patient._id ? res.data.patient : p)));
      setPaymentForm({ amount: '', method: 'cash', note: '' });
      load();
    } catch (err) {
      showToast(err.response?.data?.message || 'Could not record payment.', 'error');
    } finally {
      setRecordingPayment(false);
    }
  }

  async function applyCorrection(patientId) {
    if (!correctionReason.trim()) {
      showToast('Please enter a reason for this correction.', 'error');
      return;
    }
    setCorrecting(true);
    try {
      // For admin corrections, auto-derive status from the corrected figures
      const correctedNet   = netTotal({
        amount:     Number(correctionForm.amount),
        discount:   Number(correctionForm.discount),
        taxPercent: Number(correctionForm.taxPercent),
      });
      const correctedStatus = deriveStatus(correctedNet, Number(correctionForm.amountPaid));

      const res = await api.put(`/patients/${patientId}/billing/correction`, {
        ...correctionForm,
        amount:        Number(correctionForm.amount),
        discount:      Number(correctionForm.discount),
        taxPercent:    Number(correctionForm.taxPercent),
        amountPaid:    Number(correctionForm.amountPaid),
        paymentStatus: correctedStatus,
        dueDate:       correctionForm.dueDate || null,
        correctionReason: correctionReason.trim(),
      });
      showToast('Correction applied and logged in payment history.', 'success');
      setPatients((prev) => prev.map((p) => (p._id === patientId ? res.data.patient : p)));
      setCorrectionId(null);
      load();
    } catch (err) {
      showToast(err.response?.data?.message || 'Could not apply correction.', 'error');
    } finally {
      setCorrecting(false);
    }
  }

  // ── derived display list ───────────────────────────────────────────────────
  const afterStatusFilter = patients.filter((p) => {
    if (statusFilter !== 'all' && (p.billing?.paymentStatus || 'unpaid') !== statusFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (
        p.name.toLowerCase().includes(q) ||
        (p.patientUID || '').toLowerCase().includes(q)
      );
    }
    return true;
  });
  const totalPages = Math.max(1, Math.ceil(totalCount / limit));

  // ── Multi-day patient summary ──────────────────────────────────────────────
  const patientSummary = React.useMemo(() => {
    const map = {};
    afterStatusFilter.forEach(p => {
      const uid = p.patientUID;
      if (!map[uid]) map[uid] = { uid, name: p.name, phone: p.phone, age: p.age, gender: p.gender, visits: [] };
      const total   = netTotal(p.billing);
      const paid    = p.billing?.amountPaid || 0;
      const pending = Math.max(total - paid, 0);
      map[uid].visits.push({ id: p._id, date: p.tokenDate, invoice: p.billing?.invoiceNumber, total, paid, pending, status: p.billing?.paymentStatus || 'unpaid' });
    });
    return Object.values(map)
      .map(g => ({
        ...g,
        grandTotal:   g.visits.reduce((s,v) => s + v.total, 0),
        grandPaid:    g.visits.reduce((s,v) => s + v.paid, 0),
        grandPending: g.visits.reduce((s,v) => s + v.pending, 0),
      }))
      .sort((a,b) => {
        const [key, dir] = (sortKey||'tokenDate-desc').split('-');
        const mult = dir === 'asc' ? 1 : -1;
        if (key === 'name') return a.name.localeCompare(b.name) * mult;
        if (key === 'balance') return (a.grandPending - b.grandPending) * mult;
        if (key === 'amount') return (a.grandTotal - b.grandTotal) * mult;
        if (key === 'paymentStatus') {
          const order = { unpaid:0, partial:1, paid:2 };
          const av = order[a.visits[a.visits.length-1]?.status||'unpaid']??0;
          const bv = order[b.visits[b.visits.length-1]?.status||'unpaid']??0;
          return (av - bv) * mult;
        }
        // tokenDate — sort by most recent visit date
        const ad = a.visits.map(v=>v.date).sort().pop()||'';
        const bd = b.visits.map(v=>v.date).sort().pop()||'';
        return ad < bd ? -1*mult : ad > bd ? 1*mult : 0;
      });
  }, [afterStatusFilter, sortKey]);

  // ── render ─────────────────────────────────────────────────────────────────
  return (
    <AppLayout title="Billing" subtitle="Manage payments, invoices, and daily revenue">

      {/* Summary cards — admin only, toggleable */}
      {isAdmin && (
        <div style={{ marginBottom: showStats ? 0 : 8 }}>
          <button
            className="btn btn-outline btn-sm"
            style={{ marginBottom: showStats ? 12 : 0, display:'flex', alignItems:'center', gap:6 }}
            onClick={toggleStats}>
            {showStats ? '▲ Hide Stats' : '▼ Show Stats'}
          </button>
          {showStats && <div className="stat-grid">
            <StatCard
              icon={IndianRupee} color="primary"
              label={selectedDate === 'all' ? 'Total Billed (all time)' : `Total Billed — ${selectedDate}`}
              value={`₹${fmt(totals.billed)}`}
            />
            <StatCard
              icon={IndianRupee} color="success"
              label={selectedDate === 'all' ? 'Collected (all time)' : `Collected — ${selectedDate}`}
              value={`₹${fmt(totals.collected)}`}
            />
            <StatCard
              icon={IndianRupee} color="warning"
              label={selectedDate === 'all' ? 'Pending Balance (all time)' : `Pending Balance — ${selectedDate}`}
              value={`₹${fmt(totals.balance)}`}
            />
          </div>}
        </div>
      )}

      {/* Controls row */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        <BillingDatePicker
          selectedDate={selectedDate}
          onChange={setSelectedDate}
          availableDates={availableDates}
        />

        <div style={{ position: 'relative', flex: '1 1 200px' }}>
          <Search size={15} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--color-ink-faint)' }} />
          <input
            className="input"
            style={{ paddingLeft: 32 }}
            placeholder="Search name or patient ID…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div style={{ position: 'relative', minWidth: 200 }}>
          <ArrowUpDown size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--color-ink-faint)', pointerEvents: 'none' }} />
          <select
            className="select"
            style={{ paddingLeft: 30, fontWeight: 600 }}
            value={sortKey}
            onChange={(e) => setSortKey(e.target.value)}
          >
            {SORT_OPTIONS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
          </select>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Layers size={14} color="var(--color-ink-faint)" />
          <select
            className="select"
            style={{ minWidth: 150, fontWeight: 600 }}
            value={groupBy}
            onChange={(e) => setGroupBy(e.target.value)}
          >
            <option value="patient">Group by Patient</option>
            <option value="date">Group by Date</option>
            <option value="status">Group by Status</option>
          </select>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--color-ink-soft)', whiteSpace: 'nowrap' }}>
          Show
          <select
            className="select"
            style={{ width: 72, fontWeight: 600, padding: '8px 6px' }}
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
          >
            {PAGE_SIZE_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
          per page
        </div>
      </div>

      {/* Status filter tabs — "Unpaid" shown as "Pending" */}
      <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', marginBottom: 18 }}>
      <div style={{ display: 'flex', gap: 4, background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 10, padding: 4, width: 'fit-content', minWidth: 'max-content' }}>
        {FILTER_OPTIONS.map((opt) => {
          const count = opt.key === 'all'
            ? patients.length
            : patients.filter((p) => (p.billing?.paymentStatus || 'unpaid') === opt.key).length;
          const active = statusFilter === opt.key;
          return (
            <button
              key={opt.key}
              onClick={() => setStatusFilter(opt.key)}
              className={active ? 'btn btn-primary btn-sm' : 'btn btn-ghost btn-sm'}
            >
              {opt.label}
              <span style={{ fontSize: 11, fontWeight: 700, background: active ? 'rgba(255,255,255,0.25)' : 'var(--color-primary-soft)', color: active ? 'white' : 'var(--color-primary)', borderRadius: 100, padding: '1px 7px', marginLeft: 6 }}>
                {count}
              </span>
            </button>
          );
        })}
      </div>
      </div>



      {/* ── Merged view — all patients grouped, every detail included ─── */}
      {loading ? (
        <div style={{ display:'grid', gap:10 }}>
          {[1,2,3].map(i => <div key={i} className="skeleton" style={{ height:80, borderRadius:8 }} />)}
        </div>
      ) : afterStatusFilter.length === 0 ? (
        <div className="card" style={{ padding:48, textAlign:'center', color:'var(--color-ink-faint)' }}>
          <Receipt size={36} style={{ marginBottom:12, opacity:0.5 }} />
          <p style={{ margin:0 }}>No billing records found{selectedDate !== 'all' ? ` for ${selectedDate}` : ''}.</p>
        </div>
      ) : groupBy === 'date' ? (
        /* ── Group by Date ── */
        (() => {
          const dateMap = {};
          afterStatusFilter.forEach(p => {
            const d = p.tokenDate || 'No date';
            if (!dateMap[d]) dateMap[d] = [];
            dateMap[d].push(p);
          });
          return (
            <div style={{ display:'grid', gap:16 }}>
              {Object.entries(dateMap).sort(([a],[b]) => b>a?1:-1).map(([date, dps]) => {
                const dtotal = dps.reduce((s,p)=>s+netTotal(p.billing),0);
                const dpaid  = dps.reduce((s,p)=>s+(p.billing?.amountPaid||0),0);
                const dpend  = Math.max(dtotal-dpaid,0);
                return (
                  <div key={date}>
                    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:10, marginBottom:8, padding:'8px 14px', background:'var(--color-surface)', borderRadius:8, border:'1px solid var(--color-border)', borderLeft:'3px solid var(--color-primary)' }}>
                      <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                        <Layers size={13} color="var(--color-ink-faint)" />
                        <span style={{ fontWeight:700, fontSize:13 }}>{date}</span>
                        <span style={{ fontSize:12, color:'var(--color-ink-faint)' }}>· {dps.length} patient{dps.length>1?'s':''}</span>
                      </div>
                      <div style={{ display:'flex', gap:14 }}>
                        {[['TOTAL',dtotal,'var(--color-ink)'],['PAID',dpaid,'var(--color-success)'],['PENDING',dpend,dpend>0?'var(--color-warning)':'var(--color-ink-faint)']].map(([l,v,c])=>(
                          <div key={l} style={{ textAlign:'right' }}>
                            <div style={{ fontSize:10, color:'var(--color-ink-faint)', fontWeight:700 }}>{l}</div>
                            <div style={{ fontWeight:800, fontSize:14, color:c }}>₹{fmt(v)}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div style={{ display:'grid', gap:8 }}>
                      {[...dps].sort((a,b) => {
                        const [key, dir] = (sortKey||'tokenDate-desc').split('-');
                        const mult = dir === 'asc' ? 1 : -1;
                        if (key === 'name') return a.name.localeCompare(b.name) * mult;
                        if (key === 'balance') return (Math.max(netTotal(a.billing)-(a.billing?.amountPaid||0),0) - Math.max(netTotal(b.billing)-(b.billing?.amountPaid||0),0)) * mult;
                        if (key === 'amount') return (netTotal(a.billing) - netTotal(b.billing)) * mult;
                        const av = a.tokenDate||'', bv = b.tokenDate||'';
                        return av < bv ? -1*mult : av > bv ? 1*mult : 0;
                      }).map(p => <BillingPatientCard key={p._id} p={p} isAdmin={isAdmin} editingId={editingId} setEditingId={setEditingId} correctionId={correctionId} setCorrectionId={setCorrectionId} startEdit={startEdit} startCorrection={startCorrection} setPrintBillPatient={setPrintBillPatient} billingForm={billingForm} setBillingForm={setBillingForm} paymentForm={paymentForm} setPaymentForm={setPaymentForm} saving={saving} recordingPayment={recordingPayment} saveBilling={saveBilling} recordPayment={recordPayment} correctionForm={correctionForm} setCorrectionForm={setCorrectionForm} correctionReason={correctionReason} setCorrectionReason={setCorrectionReason} correcting={correcting} applyCorrection={applyCorrection} />)}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })()
      ) : groupBy === 'status' ? (
        /* ── Group by Payment Status ── */
        (() => {
          const statusOrder = [['unpaid','Pending'],['partial','Partial'],['paid','Paid']];
          return (
            <div style={{ display:'grid', gap:16 }}>
              {statusOrder.map(([key, label]) => {
                const grp = afterStatusFilter.filter(p=>(p.billing?.paymentStatus||'unpaid')===key);
                if (!grp.length) return null;
                const gt = grp.reduce((s,p)=>s+netTotal(p.billing),0);
                const gp = grp.reduce((s,p)=>s+(p.billing?.amountPaid||0),0);
                const gd = Math.max(gt-gp,0);
                const col = key==='paid'?'var(--color-success)':key==='partial'?'var(--color-warning)':'var(--color-danger)';
                return (
                  <div key={key}>
                    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:10, marginBottom:8, padding:'8px 14px', background:'var(--color-surface)', borderRadius:8, border:'1px solid var(--color-border)', borderLeft:`3px solid ${col}` }}>
                      <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                        <Layers size={13} color="var(--color-ink-faint)" />
                        <span style={{ fontWeight:700, fontSize:13, color:col }}>{label}</span>
                        <span style={{ fontSize:12, color:'var(--color-ink-faint)' }}>· {grp.length} patient{grp.length>1?'s':''}</span>
                      </div>
                      <div style={{ display:'flex', gap:14 }}>
                        {[['TOTAL',gt,'var(--color-ink)'],['PAID',gp,'var(--color-success)'],['PENDING',gd,gd>0?'var(--color-warning)':'var(--color-ink-faint)']].map(([l,v,c])=>(
                          <div key={l} style={{ textAlign:'right' }}>
                            <div style={{ fontSize:10, color:'var(--color-ink-faint)', fontWeight:700 }}>{l}</div>
                            <div style={{ fontWeight:800, fontSize:14, color:c }}>₹{fmt(v)}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div style={{ display:'grid', gap:8 }}>
                      {[...grp].sort((a,b) => {
                        const [key, dir] = (sortKey||'tokenDate-desc').split('-');
                        const mult = dir === 'asc' ? 1 : -1;
                        if (key === 'name') return a.name.localeCompare(b.name) * mult;
                        if (key === 'balance') return (Math.max(netTotal(a.billing)-(a.billing?.amountPaid||0),0) - Math.max(netTotal(b.billing)-(b.billing?.amountPaid||0),0)) * mult;
                        if (key === 'amount') return (netTotal(a.billing) - netTotal(b.billing)) * mult;
                        const av = a.tokenDate||'', bv = b.tokenDate||'';
                        return av < bv ? -1*mult : av > bv ? 1*mult : 0;
                      }).map(p => <BillingPatientCard key={p._id} p={p} isAdmin={isAdmin} editingId={editingId} setEditingId={setEditingId} correctionId={correctionId} setCorrectionId={setCorrectionId} startEdit={startEdit} startCorrection={startCorrection} setPrintBillPatient={setPrintBillPatient} billingForm={billingForm} setBillingForm={setBillingForm} paymentForm={paymentForm} setPaymentForm={setPaymentForm} saving={saving} recordingPayment={recordingPayment} saveBilling={saveBilling} recordPayment={recordPayment} correctionForm={correctionForm} setCorrectionForm={setCorrectionForm} correctionReason={correctionReason} setCorrectionReason={setCorrectionReason} correcting={correcting} applyCorrection={applyCorrection} />)}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })()
      ) : (
        /* ── Default: Group by Patient ── */
        <div style={{ display:'grid', gap:14 }}>
          {patientSummary.map(g => {
            // All visits for this patient in the current filtered set, sorted oldest first
            const fullVisits = afterStatusFilter
              .filter(p => p.patientUID === g.uid)
              .sort((a,b) => (a.tokenDate||'') > (b.tokenDate||'') ? 1 : -1);
            return (
              <div key={g.uid} className="card" style={{ overflow:'hidden', border:'1px solid var(--color-border)' }}>

                {/* ── Patient header — grand totals ── */}
                <div style={{ padding:'12px 16px', background:'var(--color-surface)', display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:10, borderBottom:'2px solid var(--color-primary)' }}>
                  <div>
                    <div style={{ fontWeight:800, fontSize:15, display:'flex', alignItems:'center', gap:8 }}>
                      {g.name}
                      <span style={{ fontSize:11, fontWeight:400, color:'var(--color-ink-faint)', fontFamily:'var(--font-mono)' }}>{g.uid}</span>
                    </div>
                    <div style={{ fontSize:12, color:'var(--color-ink-faint)', marginTop:2 }}>
                      {g.phone && <span>Ph: {g.phone}</span>}
                      {g.age && <span style={{ marginLeft:10 }}>{g.age}y, {g.gender}</span>}
                      <span style={{ marginLeft:10 }}>{fullVisits.length} visit{fullVisits.length>1?'s':''}</span>
                    </div>
                  </div>
                  <div style={{ display:'flex', gap:18, flexWrap:'wrap' }}>
                    {[['GRAND TOTAL', g.grandTotal, 'var(--color-ink)'],
                      ['PAID',        g.grandPaid,   'var(--color-success)'],
                      ['PENDING',     g.grandPending, g.grandPending>0?'var(--color-warning)':'var(--color-ink-faint)']
                    ].map(([lbl,val,col]) => (
                      <div key={lbl} style={{ textAlign:'right' }}>
                        <div style={{ fontSize:10, color:'var(--color-ink-faint)', fontWeight:700 }}>{lbl}</div>
                        <div style={{ fontWeight:800, fontSize:15, color:col }}>₹{fmt(val)}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* ── Each visit — full detail ── */}
                {fullVisits.map((p, vi) => {
                  const total        = netTotal(p.billing);
                  const pending      = pendingBalance(p.billing);
                  const isEditing    = editingId    === p._id;
                  const isCorrecting = correctionId === p._id;

                  return (
                    <div key={p._id} style={{ borderTop: vi===0?'none':'1px solid var(--color-border)', background: vi%2===0?'transparent':'var(--color-surface)' }}>

                      {/* Visit summary row */}
                      <div style={{ padding:'12px 16px', display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:10 }}>
                        <div style={{ flex:1, minWidth:0 }}>
                          {/* Visit header */}
                          <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4, flexWrap:'wrap' }}>
                            <span style={{ fontSize:11, color:'var(--color-ink-faint)', fontWeight:700, background:'var(--color-surface)', border:'1px solid var(--color-border)', borderRadius:4, padding:'1px 6px' }}>Visit {vi+1}</span>
                            <span style={{ fontWeight:700, fontSize:13 }}>{p.tokenDate}</span>
                            <span style={{ color:'var(--color-ink-faint)', fontSize:12 }}>Token #{p.tokenNumber}</span>
                            {p.billing?.invoiceNumber && <span style={{ fontSize:11, color:'var(--color-ink-faint)', fontFamily:'var(--font-mono)' }}>Invoice {p.billing.invoiceNumber}</span>}
                            {p.isArchived && <span style={{ fontSize:11, color:'var(--color-ink-faint)' }}>archived</span>}
                          </div>
                          {/* Patient meta */}
                          <div style={{ fontSize:12, color:'var(--color-ink-faint)', display:'flex', gap:10, flexWrap:'wrap' }}>
                            {p.appointmentSlot && (
                              <span style={{ color:'var(--color-primary)', fontWeight:600 }}>
                                {p.appointmentSlot}{p.appointmentDate ? ` · ${p.appointmentDate}` : ''}
                              </span>
                            )}
                            {p.reasonForVisit && <span style={{ fontStyle:'italic' }}>{p.reasonForVisit}</span>}
                            {p.doctorName && <span>Dr: {p.doctorName}</span>}
                          </div>
                        </div>

                        {/* Amounts + actions */}
                        <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
                          <NumCell label="Total"   value={`₹${fmt(total)}`} />
                          <NumCell label="Paid"    value={`₹${fmt(p.billing?.amountPaid)}`} color="var(--color-success)" />
                          <NumCell label="Pending" value={`₹${fmt(pending)}`} color={pending>0?'var(--color-warning)':'var(--color-ink-faint)'} />
                          <PaymentBadge status={p.billing?.paymentStatus||'unpaid'} />
                          <div style={{ display:'flex', gap:6, flexWrap:'nowrap' }}>
                            {p.phone && (
                              <WhatsAppButton
                                patient={{ ...p, _billMessage: buildBillMessage(p, total, p.billing?.amountPaid||0, pending) }}
                                slot={p.appointmentSlot}
                                date={p.tokenDate}
                                tokenNumber={p.tokenNumber}
                                size="xs"
                                billMode
                              />
                            )}
                            <button className="btn btn-outline btn-sm" onClick={() => setPrintBillPatient(p)} title="Print bill">
                              <Printer size={13} /> Print
                            </button>
                            <button className="btn btn-outline btn-sm" onClick={() => isEditing ? setEditingId(null) : startEdit(p)}>
                              {isEditing ? 'Close' : 'Manage'}
                            </button>
                            {isAdmin && (
                              <button className="btn btn-sm" style={{ background:'var(--color-warning-soft)', color:'var(--color-warning)' }}
                                onClick={() => isCorrecting ? setCorrectionId(null) : startCorrection(p)}>
                                <ShieldAlert size={13} />{isCorrecting ? 'Close' : 'Correct'}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Inline Manage (billing edit) form */}
                      {isEditing && (
                        <div style={{ padding:'0 16px 14px' }}>
                          <ManagePanel
                            p={p}
                            billingForm={billingForm}
                            setBillingForm={setBillingForm}
                            paymentForm={paymentForm}
                            setPaymentForm={setPaymentForm}
                            saving={saving}
                            recordingPayment={recordingPayment}
                            onSave={() => saveBilling(p._id, p)}
                            onRecordPayment={() => recordPayment(p)}
                          />
                        </div>
                      )}

                      {/* Inline Correct form */}
                      {isCorrecting && isAdmin && (
                        <div style={{ padding:'0 16px 14px' }}>
                          <CorrectionPanel
                            correctionForm={correctionForm}
                            setCorrectionForm={setCorrectionForm}
                            correctionReason={correctionReason}
                            setCorrectionReason={setCorrectionReason}
                            correcting={correcting}
                            onApply={() => applyCorrection(p._id)}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}

          {/* ── Single-visit patients (not in patientSummary) ── */}
          {afterStatusFilter.filter(p => {
            const cnt = afterStatusFilter.filter(q => q.patientUID === p.patientUID).length;
            return cnt === 1;
          }).length > 0 && (
            <div style={{ fontSize:12, color:'var(--color-ink-faint)', fontWeight:700, marginTop:4, paddingBottom:4, borderBottom:'1px solid var(--color-border)' }}>
              SINGLE-VISIT PATIENTS
            </div>
          )}
          {afterStatusFilter.filter(p => {
            return afterStatusFilter.filter(q => q.patientUID === p.patientUID).length === 1;
          }).sort((a,b) => {
            const [key, dir] = (sortKey||'tokenDate-desc').split('-');
            const mult = dir === 'asc' ? 1 : -1;
            if (key === 'name') return a.name.localeCompare(b.name) * mult;
            if (key === 'balance') return (Math.max(netTotal(a.billing)-(a.billing?.amountPaid||0),0) - Math.max(netTotal(b.billing)-(b.billing?.amountPaid||0),0)) * mult;
            if (key === 'amount') return (netTotal(a.billing) - netTotal(b.billing)) * mult;
            const av = (b.tokenDate||''), bv = (a.tokenDate||'');
            return av < bv ? -1*mult : av > bv ? 1*mult : 0;
          }).map((p) => {
            const total        = netTotal(p.billing);
            const pending      = pendingBalance(p.billing);
            const isEditing    = editingId    === p._id;
            const isCorrecting = correctionId === p._id;
            return (
              <div key={p._id} className="card" style={{ padding:16 }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:10 }}>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                      <div style={{ fontWeight:700, fontSize:14 }}>{p.name}</div>
                      {p.isArchived && <span style={{ fontSize:11, color:'var(--color-ink-faint)' }}>archived</span>}
                    </div>
                    <div style={{ fontSize:12, color:'var(--color-ink-faint)', fontFamily:'var(--font-mono)', marginTop:2 }}>
                      {p.patientUID}{p.billing?.invoiceNumber ? ` · Invoice ${p.billing.invoiceNumber}` : ''}{' · '}{p.tokenDate}
                    </div>
                    <div style={{ fontSize:12, color:'var(--color-ink-faint)', marginTop:2, display:'flex', gap:10, flexWrap:'wrap' }}>
                      {p.age && <span>{p.age}y, {p.gender}</span>}
                      {p.phone && <span>Ph: {p.phone}</span>}
                      {p.appointmentSlot && <span style={{ color:'var(--color-primary)', fontWeight:600 }}>{p.appointmentSlot}{p.appointmentDate?` · ${p.appointmentDate}`:''}</span>}
                      {p.reasonForVisit && <span style={{ fontStyle:'italic' }}>{p.reasonForVisit}</span>}
                    </div>
                  </div>
                  <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
                    <NumCell label="Total"   value={`₹${fmt(total)}`} />
                    <NumCell label="Paid"    value={`₹${fmt(p.billing?.amountPaid)}`} color="var(--color-success)" />
                    <NumCell label="Pending" value={`₹${fmt(pending)}`} color={pending>0?'var(--color-warning)':'var(--color-ink-faint)'} />
                    <PaymentBadge status={p.billing?.paymentStatus||'unpaid'} />
                    <div style={{ display:'flex', gap:6 }}>
                      <button className="btn btn-outline btn-sm" onClick={() => setPrintBillPatient(p)}><Printer size={13}/> Print</button>
                      <button className="btn btn-outline btn-sm" onClick={() => isEditing ? setEditingId(null) : startEdit(p)}>{isEditing ? 'Close' : 'Manage'}</button>
                      {isAdmin && <button className="btn btn-sm" style={{ background:'var(--color-warning-soft)', color:'var(--color-warning)' }} onClick={() => isCorrecting ? setCorrectionId(null) : startCorrection(p)}><ShieldAlert size={13}/>{isCorrecting ? 'Close' : 'Correct'}</button>}
                    </div>
                  </div>
                </div>
                {isEditing && (
                  <div style={{ marginTop:12 }}>
                    <ManagePanel p={p} billingForm={billingForm} setBillingForm={setBillingForm}
                      paymentForm={paymentForm} setPaymentForm={setPaymentForm} saving={saving}
                      recordingPayment={recordingPayment}
                      onSave={() => saveBilling(p._id, p)} onRecordPayment={() => recordPayment(p)} />
                  </div>
                )}
                {isCorrecting && isAdmin && (
                  <div style={{ marginTop:12 }}>
                    <CorrectionPanel correctionForm={correctionForm} setCorrectionForm={setCorrectionForm}
                      correctionReason={correctionReason} setCorrectionReason={setCorrectionReason}
                      correcting={correcting} onApply={() => applyCorrection(p._id)} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {!loading && totalCount > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 20, flexWrap: 'wrap', gap: 12 }}>
          <div style={{ fontSize: 13, color: 'var(--color-ink-soft)' }}>
            Showing{' '}
            <strong>{((page - 1) * limit) + 1}</strong>–<strong>{Math.min(page * limit, totalCount)}</strong>
            {' '}of <strong>{totalCount}</strong> records
            {selectedDate !== 'all' && <> for <strong>{selectedDate}</strong></>}
          </div>
          <div style={{ display: 'flex', gap: 4 }}>
            <PagBtn icon={ChevronsLeft}  onClick={() => setPage(1)} disabled={page === 1} title="First page" />
            <PagBtn icon={ChevronLeft}   onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} title="Previous page" />
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '0 8px', fontSize: 13, fontWeight: 600 }}>
              Page {page} of {totalPages}
            </div>
            <PagBtn icon={ChevronRight}  onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} title="Next page" />
            <PagBtn icon={ChevronsRight} onClick={() => setPage(totalPages)} disabled={page === totalPages} title="Last page" />
          </div>
        </div>
      )}
      {/* Bill print modal */}
      {printBillPatient && (
        <BillPrintModal
          patient={printBillPatient}
          onClose={() => setPrintBillPatient(null)}
        />
      )}
    </AppLayout>
  );
}

// ── small reusable sub-components ──────────────────────────────────────────────
function StatCard({ icon: Icon, color, label, value }) {
  return (
    <div className="stat-card">
      <div className="stat-card-icon" style={{ background: `var(--color-${color}-soft)` }}>
        <Icon size={18} color={`var(--color-${color})`} />
      </div>
      <div className="stat-card-label">{label}</div>
      <div className="stat-card-value">{value}</div>
    </div>
  );
}

function NumCell({ label, value, color }) {
  return (
    <div style={{ textAlign: 'right' }}>
      <div style={{ fontSize: 11, color: 'var(--color-ink-faint)' }}>{label}</div>
      <div style={{ fontWeight: 700, color: color || 'var(--color-ink)' }}>{value}</div>
    </div>
  );
}

function PagBtn({ icon: Icon, onClick, disabled, title }) {
  return (
    <button className="btn btn-icon btn-outline" onClick={onClick} disabled={disabled} title={title}>
      <Icon size={15} />
    </button>
  );
}

// Standard billing management — bill details only, NO amountPaid/status fields.
// Those come from "Record a Payment" below, keeping the history intact.
function ManagePanel({ p, billingForm, setBillingForm, paymentForm, setPaymentForm, saving, recordingPayment, onSave, onRecordPayment }) {
  const bf = (field) => (e) => setBillingForm((f) => ({ ...f, [field]: e.target.value }));
  const pf = (field) => (e) => setPaymentForm((f) => ({ ...f, [field]: e.target.value }));

  // Preview the auto-derived status as fields change so the user can see
  // what will be set without having to save first.
  const previewNet    = netTotal({
    amount:     Number(billingForm.amount),
    discount:   Number(billingForm.discount),
    taxPercent: Number(billingForm.taxPercent),
  });
  const previewStatus = deriveStatus(previewNet, p.billing?.amountPaid || 0);

  return (
    <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--color-border)' }}>
      {/* Billing details — no paymentStatus or amountPaid dropdowns here */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 14 }}>
        <F label={<><FileText size={11} style={{ marginRight: 4 }} />Invoice No.</>}>
          <input className="input" value={billingForm.invoiceNumber} onChange={bf('invoiceNumber')} placeholder="INV-1001" />
        </F>
        <F label="Bill Amount (₹)">
          <input className="input" type="number" min="0" value={billingForm.amount} onChange={bf('amount')} />
        </F>
        <F label="Discount (₹)">
          <input className="input" type="number" min="0" value={billingForm.discount} onChange={bf('discount')} />
        </F>
        <F label="Tax (%)">
          <input className="input" type="number" min="0" value={billingForm.taxPercent} onChange={bf('taxPercent')} placeholder="0" />
        </F>
        <F label={<><Calendar size={11} style={{ marginRight: 4 }} />Due Date</>}>
          <input className="input" type="date" value={billingForm.dueDate} onChange={bf('dueDate')} />
        </F>
      </div>
      <F label="Notes">
        <input className="input" value={billingForm.notes} onChange={bf('notes')} placeholder="Insurance ref, treatment notes…" />
      </F>

      {/* Auto-derived status preview */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, fontSize: 13, color: 'var(--color-ink-soft)' }}>
        <span>Net total after discount + tax:</span>
        <strong>₹{fmt(previewNet)}</strong>
        <span>→ Payment status will be auto-set to</span>
        <PaymentBadge status={previewStatus} />
      </div>

      <button className="btn btn-primary btn-sm" onClick={onSave} disabled={saving}>
        <Save size={14} />
        {saving ? 'Saving…' : 'Save Billing Details'}
      </button>

      {/* Record a Payment section */}
      <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--color-border)' }}>
        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10 }}>Record a Payment</div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <F label="Amount (₹)" style={{ width: 120, marginBottom: 0 }}>
            <input className="input" type="number" min="0" value={paymentForm.amount} onChange={pf('amount')} placeholder="0" />
          </F>
          <F label="Method" style={{ width: 130, marginBottom: 0 }}>
            <select className="select" value={paymentForm.method} onChange={pf('method')}>
              <option value="cash">Cash</option>
              <option value="card">Card</option>
              <option value="upi">UPI</option>
              <option value="insurance">Insurance</option>
              <option value="other">Other</option>
            </select>
          </F>
          <F label="Note" style={{ flex: 1, minWidth: 140, marginBottom: 0 }}>
            <input className="input" value={paymentForm.note} onChange={pf('note')} placeholder="Receipt #…" />
          </F>
          <button
            className="btn btn-primary btn-sm"
            onClick={onRecordPayment}
            disabled={recordingPayment}
            style={{ alignSelf: 'flex-end', marginBottom: 0 }}
          >
            <Plus size={14} />
            {recordingPayment ? 'Recording…' : 'Add Payment'}
          </button>
        </div>
        <PaymentHistory history={p.billing?.paymentHistory} patient={p} />
      </div>
    </div>
  );
}

// Admin-only correction panel — has full manual control including paymentStatus
function CorrectionPanel({ correctionForm, setCorrectionForm, correctionReason, setCorrectionReason, correcting, onApply }) {
  const cf = (field) => (e) => setCorrectionForm((f) => ({ ...f, [field]: e.target.value }));

  const previewNet    = netTotal({
    amount:     Number(correctionForm.amount),
    discount:   Number(correctionForm.discount),
    taxPercent: Number(correctionForm.taxPercent),
  });
  const previewStatus = deriveStatus(previewNet, Number(correctionForm.amountPaid));

  return (
    <div style={{ marginTop: 16, paddingTop: 16, borderTop: '2px solid var(--color-warning)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
        <ShieldAlert size={16} color="var(--color-warning)" />
        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--color-warning)' }}>Admin Payment Correction</span>
        <span style={{ fontSize: 12, color: 'var(--color-ink-faint)' }}>— Use only to fix entries made in error. All changes are logged.</span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 14 }}>
        <F label="Bill Amount (₹)"><input className="input" type="number" value={correctionForm.amount} onChange={cf('amount')} /></F>
        <F label="Discount (₹)"><input className="input" type="number" value={correctionForm.discount} onChange={cf('discount')} /></F>
        <F label="Tax (%)"><input className="input" type="number" value={correctionForm.taxPercent} onChange={cf('taxPercent')} /></F>
        <F label="Amount Paid (₹)"><input className="input" type="number" value={correctionForm.amountPaid} onChange={cf('amountPaid')} /></F>
        <F label="Payment Method">
          <select className="select" value={correctionForm.paymentMethod} onChange={cf('paymentMethod')}>
            <option value="none">Not specified</option>
            <option value="cash">Cash</option>
            <option value="card">Card</option>
            <option value="upi">UPI</option>
            <option value="insurance">Insurance</option>
            <option value="other">Other</option>
          </select>
        </F>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, fontSize: 13, color: 'var(--color-ink-soft)' }}>
        <span>Net total after correction:</span>
        <strong>₹{fmt(previewNet)}</strong>
        <span>→ Status will be auto-set to</span>
        <PaymentBadge status={previewStatus} />
      </div>
      <F label="Invoice Number">
        <input className="input" value={correctionForm.invoiceNumber || ''} onChange={cf('invoiceNumber')} placeholder="INV-XXXX" />
      </F>
      <F label="Notes">
        <input className="input" value={correctionForm.notes || ''} onChange={cf('notes')} />
      </F>
      <F label="Reason for correction (required)">
        <input
          className="input"
          value={correctionReason}
          onChange={(e) => setCorrectionReason(e.target.value)}
          placeholder="e.g. Wrong amount entered — correcting ₹1500 to ₹1200"
          style={{ borderColor: !correctionReason ? 'var(--color-warning)' : undefined }}
        />
      </F>
      <button
        className="btn btn-sm"
        style={{ background: 'var(--color-warning)', color: 'white' }}
        onClick={onApply}
        disabled={correcting || !correctionReason.trim()}
      >
        <ShieldAlert size={14} />
        {correcting ? 'Applying…' : 'Apply Correction'}
      </button>
    </div>
  );
}

// Payment history with patient details and full timestamp
function PaymentHistory({ history, patient }) {
  if (!history?.length) return null;
  return (
    <div style={{ marginTop: 14 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--color-ink-faint)', display: 'flex', alignItems: 'center', gap: 5, marginBottom: 8 }}>
        <History size={13} /> Payment History
      </div>

      {/* Patient summary at the top of history, for context when printing / referencing */}
      <div style={{ fontSize: 12, padding: '8px 10px', background: 'var(--color-primary-soft)', borderRadius: 6, marginBottom: 8, display: 'flex', gap: 16, flexWrap: 'wrap', color: 'var(--color-primary)' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <User size={12} /> {patient?.name}
        </span>
        {patient?.patientUID && <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>{patient.patientUID}</span>}
        {patient?.age && <span>{patient.age}y, {patient.gender}</span>}
        {patient?.phone && <span>📱 {patient.phone}</span>}
        {patient?.tokenDate && <span>Visit: {patient.tokenDate}</span>}
        {patient?.reasonForVisit && <span style={{ fontStyle: 'italic', color: 'var(--color-primary)', opacity: 0.8 }}>{patient.reasonForVisit}</span>}
      </div>

      <div style={{ display: 'grid', gap: 6 }}>
        {[...history].reverse().map((entry) => (
          <div
            key={entry._id}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              fontSize: 12,
              padding: '8px 10px',
              background: entry.amount === 0 ? 'var(--color-warning-soft)' : 'var(--color-bg)',
              borderRadius: 6,
              gap: 10,
            }}
          >
            <span style={{ flex: 1 }}>
              {entry.amount > 0
                ? <strong>₹{entry.amount.toLocaleString('en-IN')}</strong>
                : <span style={{ color: 'var(--color-warning)', fontWeight: 700 }}>⚙ Correction</span>}
              {entry.amount > 0 && <span style={{ color: 'var(--color-ink-faint)' }}> · {entry.method}</span>}
              {entry.note && <span style={{ color: 'var(--color-ink-faint)' }}> · {entry.note}</span>}
            </span>
            <div style={{ textAlign: 'right', color: 'var(--color-ink-faint)', flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, justifyContent: 'flex-end' }}>
                <Clock size={11} />
                {fmtDateTime(entry.paidAt)}
              </div>
              {entry.recordedBy && <div style={{ fontSize: 11 }}>by {entry.recordedBy}</div>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Tiny field wrapper
function F({ label, children, style }) {
  return (
    <div className="field" style={{ marginBottom: 12, ...style }}>
      <label className="label">{label}</label>
      {children}
    </div>
  );
}
